import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Video, Home, Building2 } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
/**
 * SmartFilterToggle
 * Horizontal pill-switch for "Online / Home / Centre".
 * Remembers choice in localStorage and navigates to search with ?mode=...
 */
const SmartFilterToggle = ({ language = 'ar', isDark = false }) => {
  const { localize } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAr = language === 'ar';
  const active = searchParams.get('mode') || localStorage.getItem('preferred_mode') || 'any';

  const options = [
    { key: 'any',     icon: null,       ar: 'الكل',           en: 'Any mode'  },
    { key: 'online',  icon: Video,      ar: 'أونلاين',        en: 'Online'    },
    { key: 'home',    icon: Home,       ar: 'في المنزل',      en: 'At home'   },
    { key: 'center',  icon: Building2,  ar: 'مركز',           en: 'Centre'    },
  ];

  const pick = (key) => {
    localStorage.setItem('preferred_mode', key);
    const suffix = key === 'any' ? '' : `?mode=${key}`;
    navigate(`/tutors${suffix}`);
  };

  return (
    <section className={`py-10 ${isDark ? 'bg-[#09090B]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'} data-testid="smart-filter-toggle-section">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={`flex items-center justify-center gap-1 p-1.5 rounded-full border shadow-[0_2px_10px_rgba(15,23,42,0.04)] ${
            isDark ? 'bg-[#161b22] border-[#30363d]' : 'bg-white border-slate-100'
          }`}
        >
          <span className={`text-xs font-semibold px-3 hidden sm:inline ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {localize({ ar: 'أبحث عن:', en: 'Looking for:' })}
          </span>
          {options.map((opt) => {
            const Icon = opt.icon;
            const isActive = active === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => pick(opt.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${
                  isActive
                    ? 'bg-indigo-600 text-white shadow-[0_4px_14px_rgba(79,70,229,0.3)]'
                    : isDark
                      ? 'text-slate-300 hover:bg-white/5'
                      : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`mode-toggle-${opt.key}`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {localize({ ar: opt.ar, en: opt.en })}
              </button>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default SmartFilterToggle;
